/* ==========================================================================
   Rook UI Core — Sheet Controller
   Web Component: <rk-sheet-element>
   ========================================================================== */

if (!customElements.get('rk-sheet-element')) {
    class RkSheetElement extends HTMLElement {
        constructor() {
            super();
            this.sheet = null;
            this.overlay = null;
            this.panel = null;
            this.previousFocus = null;

            this._onKeyDown = this._onKeyDown.bind(this);
        }

        connectedCallback() {
            this.sheet = this.querySelector('.rk-sheet');
            this.overlay = this.querySelector('.rk-sheet__overlay');
            this.panel = this.querySelector('.rk-sheet__panel');

            if (!this.sheet || !this.panel) return;

            this._bindEvents();
            this._setupOpenTriggers();
        }

        /* ------------------------------------------------------------------ */
        /*  Events                                                            */
        /* ------------------------------------------------------------------ */

        _bindEvents() {
            // Close buttons
            this.querySelectorAll('[data-action="close"]').forEach((el) => {
                el.addEventListener('click', () => this.close());
            });

            // Overlay click (configurable)
            if (this.overlay) {
                const overlayClose = this.dataset.overlayClose !== 'false';
                if (overlayClose) {
                    this.overlay.addEventListener('click', () => this.close());
                }
            }
        }

        _setupOpenTriggers() {
            const sheetId = this.dataset.sheetId || this.sheet?.id;
            if (!sheetId) return;

            document.querySelectorAll(`[data-sheet-open="${sheetId}"]`).forEach((trigger) => {
                trigger.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.open();
                });
            });
        }

        /* ------------------------------------------------------------------ */
        /*  Public API                                                        */
        /* ------------------------------------------------------------------ */

        isOpen() {
            return this.sheet?.classList.contains('rk-sheet--active');
        }

        open() {
            if (!this.sheet || this.isOpen()) return;

            this.previousFocus = document.activeElement;
            this.sheet.classList.add('rk-sheet--active');
            document.body.style.overflow = 'hidden';
            document.addEventListener('keydown', this._onKeyDown);

            // Focus first interactive element
            requestAnimationFrame(() => {
                const firstFocusable = this.panel.querySelector(
                    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                );
                if (firstFocusable) {
                    firstFocusable.focus();
                }
            });

            this.dispatchEvent(
                new CustomEvent('rk:sheet:open', {
                    bubbles: true,
                    detail: { id: this.sheet.id },
                })
            );
        }

        close() {
            if (!this.sheet || !this.isOpen()) return;

            this.sheet.classList.remove('rk-sheet--active');
            document.body.style.overflow = '';
            document.removeEventListener('keydown', this._onKeyDown);

            if (this.previousFocus) {
                this.previousFocus.focus();
                this.previousFocus = null;
            }

            this.dispatchEvent(
                new CustomEvent('rk:sheet:close', {
                    bubbles: true,
                    detail: { id: this.sheet.id },
                })
            );
        }

        /* ------------------------------------------------------------------ */
        /*  Keyboard (ESC + Tab trap)                                         */
        /* ------------------------------------------------------------------ */

        _onKeyDown(e) {
            if (!this.isOpen()) return;

            if (e.key === 'Escape') {
                e.preventDefault();
                this.close();
                return;
            }

            if (e.key === 'Tab') {
                this._trapFocus(e);
            }
        }

        _trapFocus(e) {
            const focusable = this.panel?.querySelectorAll(
                'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
            );

            if (!focusable || focusable.length === 0) return;

            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (e.shiftKey) {
                if (document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                }
            } else {
                if (document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        }
    }

    customElements.define('rk-sheet-element', RkSheetElement);
}
